import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://guiadejampa.com";
const DEFAULT_IMG = `${SITE}/icon-512.png`;

/**
 * Central SEO component (react-helmet-async).
 *
 * Props:
 *  - title / description / image / keywords: standard meta
 *  - path: appended to SITE for canonical/og:url (e.g. "/empresa/x")
 *  - canonicalPath: overrides canonical only (used by query-filter pages
 *    that consolidate authority into a landing SEO page)
 *  - jsonLd: object OR array of objects to emit as JSON-LD
 *  - type: og:type override (default "website")
 *  - noindex: emit robots="noindex,follow" (+ googlebot mirror)
 *  - nofollow: when true and noindex is set, emits "noindex,nofollow"
 */
export default function SEO({
  title, description, path = "", image, jsonLd, keywords,
  canonicalPath, type = "website", noindex = false, nofollow = false,
}) {
  const canonical = `${SITE}${canonicalPath || path}`;
  const url = `${SITE}${path}`;
  const fullTitle = title ? `${title} | Guia de Jampa AI` : "Guia de Jampa AI — A IA Oficial de João Pessoa";
  const desc = description || "Tudo o que acontece em João Pessoa, explicado pela IA. Restaurantes, eventos, praias, hotéis, turismo e empresas da Paraíba.";
  const robots = noindex ? (nofollow ? "noindex, nofollow" : "noindex, follow") : "index, follow";
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image || DEFAULT_IMG} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image || DEFAULT_IMG} />
      {jsonLdArray.map((obj) => {
        const json = JSON.stringify(obj);
        // Stable key derived from @type + name/url so React can reconcile even if order changes.
        const key = `${obj?.["@type"] || "ld"}-${obj?.name || obj?.url || json.length}`;
        return <script key={key} type="application/ld+json">{json}</script>;
      })}
    </Helmet>
  );
}
